import React, { useState, useEffect } from 'react';
import styles from './ActiveSessions.module.css';
import { format } from 'date-fns';
import { sessionService } from '../../../services/api';

const ActiveSessions = ({ isOpen, onClose }) => {
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    if (isOpen) {
      fetchSessionData();
    }
  }, [isOpen]);

  const fetchSessionData = async () => {
    try {
      setLoading(true);
      const [sessionsResponse, statsResponse] = await Promise.all([
        sessionService.getActiveSessions(),
        sessionService.getSessionStats()
      ]);

      if (sessionsResponse.data.status === 'success') {
        setSessions(sessionsResponse.data.sessions);
      }

      if (statsResponse.data.status === 'success') {
        setStats(statsResponse.data.stats);
      }
    } catch (err) {
      console.error('Error fetching session data:', err);
      setError('Failed to load session data');
    } finally {
      setLoading(false);
    }
  };

  const revokeSession = async (sessionId) => {
    try {
      setActionLoading(prev => ({ ...prev, [sessionId]: true }));
      
      const response = await sessionService.revokeSession(sessionId);
      
      if (response.data.status === 'success') {
        // Remove the session from the list
        setSessions(prev => prev.filter(session => session.id !== sessionId));
        // Update stats
        await fetchSessionData();
      }
    } catch (err) {
      console.error('Error revoking session:', err);
      setError('Failed to revoke session');
    } finally {
      setActionLoading(prev => ({ ...prev, [sessionId]: false }));
    }
  };

  const revokeAllOtherSessions = async () => {
    try {
      setActionLoading(prev => ({ ...prev, revokeAll: true }));
      
      const response = await sessionService.revokeAllOtherSessions();
      
      if (response.data.status === 'success') {
        // Refresh the session list
        await fetchSessionData();
      }
    } catch (err) {
      console.error('Error revoking all sessions:', err);
      setError('Failed to revoke all sessions');
    } finally {
      setActionLoading(prev => ({ ...prev, revokeAll: false }));
    }
  };

  const getDeviceDisplayName = (session) => {
    if (session.device_info) {
      return session.device_info;
    }
    return `${session.browser} on ${session.operating_system}`;
  };

  const formatLastSeen = (lastActivity) => {
    const lastSeen = new Date(lastActivity);
    const now = new Date();
    const diffInMinutes = Math.floor((now - lastSeen) / (1000 * 60));

    if (diffInMinutes < 5) {
      return 'Active now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minutes ago`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      return format(lastSeen, 'MMM dd, yyyy');
    }
  };

  const getBrandIconClass = (iconName) => {
    // Brand icons (browsers) use 'fab', others use 'fas'
    const brandIcons = [
      'fa-chrome', 'fa-firefox', 'fa-safari', 'fa-edge', 'fa-opera', 
      'fa-internet-explorer', 'fa-apple', 'fa-windows', 'fa-linux',
      'fa-android', 'fa-google'
    ];
    
    return brandIcons.includes(iconName) ? 'fab' : 'fas';
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2>Active Sessions</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className={styles.modalContent}>
          {loading ? (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <span>Loading active sessions...</span>
            </div>
          ) : error ? (
            <div className={styles.error}>
              <i className="fas fa-exclamation-circle"></i>
              {error}
            </div>
          ) : (
            <>
              {/* Session Statistics */}
              <div className={styles.statsSection}>
                <div className={styles.statsGrid}>
                  <div className={styles.statCard}>
                    <div className={styles.statValue}>{stats.active_sessions || 0}</div>
                    <div className={styles.statLabel}>Active Sessions</div>
                  </div>
                  <div className={styles.statCard}>
                    <div className={styles.statValue}>{stats.online_sessions || 0}</div>
                    <div className={styles.statLabel}>Online Now</div>
                  </div>
                  <div className={styles.statCard}>
                    <div className={styles.statValue}>{stats.unique_devices || 0}</div>
                    <div className={styles.statLabel}>Unique Devices</div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className={styles.actionSection}>
                <button
                  className={styles.revokeAllButton}
                  onClick={revokeAllOtherSessions}
                  disabled={actionLoading.revokeAll || sessions.filter(s => !s.is_current).length === 0}
                >
                  {actionLoading.revokeAll ? (
                    <>
                      <div className={styles.smallSpinner}></div>
                      Revoking...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-sign-out-alt"></i>
                      Sign out of all other sessions
                    </>
                  )}
                </button>
                <div className={styles.actionHelp}>
                  This will sign you out of all other devices except this one.
                </div>
              </div>

              {/* Sessions List */}
              <div className={styles.sessionsList}>
                <h3>Your active sessions</h3>
                {sessions.map((session) => (
                  <div key={session.id} className={`${styles.sessionItem} ${session.is_current ? styles.currentSession : ''}`}>
                    <div className={styles.sessionIcon}>
                      <i className={`${getBrandIconClass(session.device_icon)} ${session.device_icon || 'fa-desktop'}`}></i>
                    </div>
                    
                    <div className={styles.sessionDetails}>
                      <div className={styles.sessionName}>
                        {getDeviceDisplayName(session)}
                        {session.is_current && (
                          <span className={styles.currentLabel}>
                            <i className="fas fa-check-circle"></i>
                            This device
                          </span>
                        )}
                      </div>
                      
                      <div className={styles.sessionMeta}>
                        <div className={styles.sessionLocation}>
                          <i className="fas fa-map-marker-alt"></i>
                          {session.location}
                        </div>
                        <div className={styles.sessionActivity}>
                          <i className="fas fa-clock"></i>
                          <span className={session.is_online ? styles.onlineStatus : ''}>
                            {formatLastSeen(session.last_activity)}
                          </span>
                          {session.is_online && (
                            <span className={styles.onlineDot}></span>
                          )}
                        </div>
                        <div className={styles.sessionIP}>
                          <i className="fas fa-network-wired"></i>
                          {session.ip_address}
                        </div>
                      </div>
                    </div>
                    
                    <div className={styles.sessionActions}>
                      {!session.is_current && (
                        <button
                          className={styles.revokeButton}
                          onClick={() => revokeSession(session.id)}
                          disabled={actionLoading[session.id]}
                          title="Sign out this session"
                        >
                          {actionLoading[session.id] ? (
                            <div className={styles.smallSpinner}></div>
                          ) : (
                            <i className="fas fa-sign-out-alt"></i>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActiveSessions; 